/**
 * O modal de troca de contexto — a lista que o gatilho do cabeçalho abre.
 *
 * Ele existe por escala: quem opera a Plataforma alcança todo cliente dela, e um menu suspenso
 * com centenas de empresas e clínicas juntas não é navegável. Aqui a lista chega separada por
 * tipo, com busca e com um teto de itens desenhados.
 *
 * A lista ainda vem inteira dentro do manifesto: a busca filtra na memória. Quando ela passar a
 * ser perguntada ao servidor, o que muda é `filtrarContextos` — esta tela não.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Close as CloseIcon,
  MedicalServices as MedicalServicesIcon,
  Public as PublicIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { useManifesto } from '../../stores/manifestoStore';
import type { ContextoDisponivel, Perspectiva } from '../../types/manifesto';
import { filtrarContextos } from '../../utils/buscaDeContextos';
import { serializarContexto } from '../../utils/manifestoMenu';

/**
 * Desenhar seiscentas linhas de uma vez trava a abertura do modal, e a alternativa seria trazer
 * uma biblioteca de rolagem virtual só para isto. Com aba e busca, quem passa deste teto já
 * está procurando alguém pelo nome.
 */
export const TETO_DA_LISTA = 50;

const ICONE_DO_TIPO: Record<Perspectiva, typeof BusinessIcon> = {
  empresa: BusinessIcon,
  prestador: MedicalServicesIcon,
  plataforma: PublicIcon,
};

/** A Plataforma fica fora: ela é sempre um item só, e uma aba com um item é um clique à toa. */
const TIPOS_COM_ABA = ['empresa', 'prestador'] as const;

type TipoComAba = (typeof TIPOS_COM_ABA)[number];

const ROTULO_DA_ABA: Record<TipoComAba, string> = {
  empresa: 'Empresas',
  prestador: 'Clínicas',
};

interface Props {
  aberto: boolean;
  aoFechar: () => void;
}

const ContextSwitcherModal: React.FC<Props> = ({ aberto, aoFechar }) => {
  const { manifesto, trocarContexto } = useManifesto();
  const navigate = useNavigate();
  const tema = useTheme();
  const emCelular = useMediaQuery(tema.breakpoints.down('sm'));

  const [busca, setBusca] = useState('');
  const [aba, setAba] = useState<TipoComAba>('empresa');
  const [destacado, setDestacado] = useState(0);
  const [trocando, setTrocando] = useState<string | null>(null);
  const itemDestacado = useRef<HTMLDivElement | null>(null);

  const contextos = useMemo(() => manifesto?.contextos ?? [], [manifesto]);
  const plataforma = contextos.find((contexto) => contexto.tipo === 'plataforma');

  const porTipo = useMemo(
    () => ({
      empresa: contextos.filter((contexto) => contexto.tipo === 'empresa'),
      prestador: contextos.filter((contexto) => contexto.tipo === 'prestador'),
    }),
    [contextos]
  );

  const filtrados = useMemo(
    () => ({
      empresa: filtrarContextos(porTipo.empresa, busca),
      prestador: filtrarContextos(porTipo.prestador, busca),
    }),
    [porTipo, busca]
  );

  const abasVisiveis = TIPOS_COM_ABA.filter((tipo) => porTipo[tipo].length > 0);
  const abaAtual = abasVisiveis.includes(aba) ? aba : (abasVisiveis[0] ?? 'empresa');
  const visiveis = filtrados[abaAtual];
  const listados = visiveis.slice(0, TETO_DA_LISTA);

  // Cada abertura recomeça na aba de onde a pessoa está, sem a busca da vez passada.
  useEffect(() => {
    if (!aberto || !manifesto) return;

    const tipoAtivo = manifesto.contextoAtivo.tipo;
    setBusca('');
    setAba(tipoAtivo === 'plataforma' ? 'empresa' : tipoAtivo);
  }, [aberto, manifesto]);

  useEffect(() => {
    setDestacado(0);
  }, [busca, abaAtual]);

  useEffect(() => {
    itemDestacado.current?.scrollIntoView?.({ block: 'nearest' });
  }, [destacado]);

  if (!manifesto) return null;

  const ehOAtivo = (contexto: ContextoDisponivel) =>
    contexto.tipo === manifesto.contextoAtivo.tipo && contexto.id === manifesto.contextoAtivo.id;

  // A tela alcançável muda com o contexto, e ficar onde estava vira uma recusa que a pessoa não
  // liga à troca. A ida à tela inicial espera o manifesto novo, senão o menu ainda seria o velho.
  const escolher = async (contexto: ContextoDisponivel) => {
    if (ehOAtivo(contexto)) {
      aoFechar();
      return;
    }

    setTrocando(serializarContexto(contexto));
    try {
      await trocarContexto(contexto);
      aoFechar();
      navigate('/', { replace: true });
    } finally {
      setTrocando(null);
    }
  };

  const aoTeclarNaBusca = (evento: React.KeyboardEvent) => {
    if (evento.key === 'ArrowDown') {
      evento.preventDefault();
      setDestacado((atual) => Math.min(atual + 1, listados.length - 1));
      return;
    }
    if (evento.key === 'ArrowUp') {
      evento.preventDefault();
      setDestacado((atual) => Math.max(atual - 1, 0));
      return;
    }
    if (evento.key === 'Enter') {
      evento.preventDefault();
      const alvo = listados[destacado];
      if (alvo) void escolher(alvo);
    }
  };

  const linhaDo = (contexto: ContextoDisponivel, opcoes?: { destacar?: boolean }) => {
    const chave = serializarContexto(contexto);
    const Icone = ICONE_DO_TIPO[contexto.tipo];
    const ativo = ehOAtivo(contexto);

    return (
      <ListItemButton
        key={chave}
        ref={opcoes?.destacar ? itemDestacado : undefined}
        selected={opcoes?.destacar}
        disabled={Boolean(trocando)}
        onClick={() => void escolher(contexto)}
      >
        <ListItemIcon sx={{ minWidth: 40 }}>
          <Icone fontSize='small' color={ativo ? 'primary' : 'action'} />
        </ListItemIcon>
        <ListItemText
          primary={contexto.nome}
          secondary={contexto.detalhe}
          slotProps={{ primary: { noWrap: true }, secondary: { noWrap: true } }}
        />
        {ativo && <Chip label='Atual' size='small' color='primary' variant='outlined' />}
        {trocando === chave && <CircularProgress size={18} sx={{ ml: 1 }} />}
      </ListItemButton>
    );
  };

  return (
    <Dialog
      open={aberto}
      onClose={aoFechar}
      fullWidth
      maxWidth='sm'
      fullScreen={emCelular}
      aria-labelledby='titulo-troca-de-contexto'
    >
      <DialogTitle
        id='titulo-troca-de-contexto'
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
      >
        Trocar de contexto
        <IconButton onClick={aoFechar} aria-label='Fechar' size='small'>
          <CloseIcon fontSize='small' />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <TextField
            size='small'
            fullWidth
            autoFocus
            placeholder='Buscar por nome ou CNPJ'
            value={busca}
            onChange={(evento) => setBusca(evento.target.value)}
            onKeyDown={aoTeclarNaBusca}
            inputProps={{ 'aria-label': 'Buscar contexto' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon fontSize='small' color='action' />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {plataforma && (
          <>
            <List disablePadding>{linhaDo(plataforma)}</List>
            <Divider />
          </>
        )}

        {abasVisiveis.length > 1 && (
          <Tabs
            value={abaAtual}
            onChange={(_, escolhida: TipoComAba) => setAba(escolhida)}
            variant='fullWidth'
          >
            {abasVisiveis.map((tipo) => (
              <Tab
                key={tipo}
                value={tipo}
                label={`${ROTULO_DA_ABA[tipo]} (${filtrados[tipo].length})`}
              />
            ))}
          </Tabs>
        )}

        {listados.length === 0 ? (
          <Typography variant='body2' color='text.secondary' sx={{ p: 3, textAlign: 'center' }}>
            Nenhum contexto encontrado
          </Typography>
        ) : (
          <List disablePadding sx={{ maxHeight: 360, overflowY: 'auto' }}>
            {listados.map((contexto, indice) => linhaDo(contexto, { destacar: indice === destacado }))}
          </List>
        )}

        {visiveis.length > TETO_DA_LISTA && (
          <Typography variant='caption' color='text.secondary' sx={{ p: 2, display: 'block' }}>
            {`Mostrando ${TETO_DA_LISTA} de ${visiveis.length} — refine a busca para achar o resto`}
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ContextSwitcherModal;
